import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import api from "../../services/api";
import { queryKeys } from "../api/queryKeys";
import { useAppContext } from "../../contexts/AppContext";
import { handleApiError } from "../../utils/handleApiError";
import { CancelOrderResponse } from "../../types/api/OrderResponse";

export const useCancelOrder = () => {
  const queryClient = useQueryClient();
  const { nrPedidoAtivo, setNrPedidoAtivo, getCampanhaSolarValue } =
    useAppContext();

  return useMutation<CancelOrderResponse, unknown, void>({
    mutationFn: async () => {
      const endpoint =
        nrPedidoAtivo !== null ? `orders/${nrPedidoAtivo}` : "orders";

      const response = await api.delete<CancelOrderResponse>(endpoint);

      if (response.status !== 200) {
        throw new Error("Não foi possível cancelar o pedido");
      }

      return response.data;
    },
    onSuccess: () => {
      // Toast de sucesso
      toast.success("Seu pedido foi cancelado!");

      // Remove o número do pedido ativo do Context (que sincroniza com localStorage)
      setNrPedidoAtivo(null);

      // Obtém o valor atual da campanha solar do Context
      const isCampanhaSolar = getCampanhaSolarValue();

      // Limpa o cache definindo dados vazios (como o Redux faz)
      const emptyOrderData = {
        nr_pedido: "",
        items: [],
        clientInfo: {
          session: {
            bloqueio_pedido: { podeFechar: null, msgs: [] },
            cliente: { vlrMinimo: 50 },
          },
        },
      };

      // Atualiza o cache com dados vazios
      queryClient.setQueryData(
        queryKeys.order(isCampanhaSolar),
        emptyOrderData
      );

      // Invalida apenas a query específica do isCampanhaSolar atual
      // Isso evita refetch de ambas as queries (S e N)
      queryClient.invalidateQueries({
        queryKey: queryKeys.order(isCampanhaSolar),
      });
    },
    onError: (error: unknown) => {
      handleApiError(error, "Não foi possível cancelar o pedido");
    },
  });
};
